import React, {
	type ElementType,
	type ForwardedRef,
	forwardRef,
	useMemo,
} from "react";
import { pushToDataLayer } from "../../utils/google";

export interface DataLayerWrapperProps
	extends React.ComponentPropsWithRef<ElementType> {
	event: Google.DataLayerEventName;
	menu_level: "top" | "sub" | "footer";
	menu_clicked: string;
	menu_url: string;
	menu_location: string;
}

export const DataLayerWrapper = forwardRef(
	(
		{
			children,
			event,
			menu_level,
			menu_clicked,
			menu_url,
		}: DataLayerWrapperProps,
		ref: ForwardedRef<unknown>,
	) => {
		const AnalyticsAugmentedChild = useMemo(() => {
			// We need a single child
			const AugmentationCandidate = React.Children.only(children);

			if (!AugmentationCandidate) {
				return;
			}

			if (!React.isValidElement(AugmentationCandidate)) {
				return AugmentationCandidate;
			}

			const callAnalytics = () => {
				pushToDataLayer({
					event: event,
					menu_level: menu_level,
					menu_clicked: menu_clicked,
					menu_url: menu_url,
					menu_location: window.location.href,
				});
			};

			// We only need to proxy the click event because react triggers a click for us
			const { onClick, ...rest } = AugmentationCandidate.props as {
				onClick: (event: unknown) => void;
			};

			return (
				<AugmentationCandidate.type
					{...rest}
					onClick={(event: React.MouseEvent) => {
						callAnalytics();
						onClick?.(event);
					}}
					ref={ref}
				/>
			);
		}, [children, event, menu_level, menu_clicked, menu_url, ref]);

		return <>{AnalyticsAugmentedChild}</>;
	},
);
